import React from "react";
import { cn } from "@/lib/utils";
import { directionOf, pick } from "@/lib/languages";
import { useFormat } from "@/lib/format";
import { dsL } from "@/i18n/ds";

/**
 * One price, total first.
 *
 * The large figure is the total for the dates, travellers and options chosen.
 * What it is made of comes second, smaller: "3 nuits, 2 voyageurs · 137 € / nuit".
 * Prices are ink, never red, in tabular figures so a column of them lines up.
 *
 * - `amount`: the total in XOF (the stored currency); printed in the visitor's
 *   currency by lib/format. A missing or unknown amount renders nothing, never
 *   a free "0".
 * - `unit`: { amount, per: "night" | "day" | "person" }, the second line's rate.
 * - `context`: what the total covers, already worded ("3 nuits, 2 voyageurs").
 * - `charge`: { amount, currency } exactly as the server states the debit
 *   ("Débité : 481,20 USD"). Not converted here.
 * - `serverOriginal`: a struck-through former total, shown only when the server
 *   computed it (a real promotion) and it is higher than `amount`. Never
 *   derived on the client.
 * - `note`: a fact about the total that the data supports ("taxes included").
 * - `size`: lg (results, summaries) · md (sticky bar) · sm (dense rows).
 * - `align`: end · start · responsive (start on phones, end from 640 px, for a
 *   result card's price column).
 */
// The price scale of tailwind.config.js (24/28, 18/24, 15/20, bold, tabular).
const SIZES = {
  lg: "text-price-lg",
  md: "text-price-md",
  sm: "text-price-sm",
};
const ALIGN = {
  start: "items-start text-start",
  end: "items-end text-end",
  responsive: "items-start text-start sm:items-end sm:text-end",
};
const ROW = {
  start: "justify-start",
  end: "justify-end",
  responsive: "justify-start sm:justify-end",
};

const known = (value) => value != null && value !== "" && Number.isFinite(Number(value));

// An amount is a left-to-right run ("270 250 FCFA", "$20", "481.20 USD").
// Inside an Arabic sentence it is isolated (LRI ... PDI), or the bidi
// algorithm prints "FCFA 270 250". Elements carry dir="ltr" instead.
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);

export default function Price({
  amount, unit, context, charge, serverOriginal, note, size = "lg", align = "end", className,
  "data-testid": testId = "price", ...rest
}) {
  const f = useFormat();
  const t = pick(dsL, f.lang).price;
  if (!known(amount)) return null;
  const isolate = (text) => (directionOf(f.lang) === "rtl" ? `${LRI}${text}${PDI}` : text);
  const original = known(serverOriginal) && Number(serverOriginal) > Number(amount) ? serverOriginal : null;
  const rate = unit && known(unit.amount) && t.per[unit.per] ? t.unit(isolate(f.money(unit.amount)), t.per[unit.per]) : null;
  const debit = charge && known(charge.amount) ? f.charge(charge.amount, charge.currency) : "";
  return (
    <div data-testid={testId} className={cn("flex flex-col gap-0.5 text-touki-black", ALIGN[align] || ALIGN.end, className)} {...rest}>
      <p className={cn("flex flex-wrap items-baseline gap-x-2", ROW[align] || ROW.end)}>
        {original ? (
          <s className="text-body-sm text-touki-muted tabular-nums" data-testid="price-original">
            <span className="sr-only">{t.original} </span><span dir="ltr">{f.money(original)}</span>
          </s>
        ) : null}
        <span dir="ltr" className={cn("font-bold tabular-nums whitespace-nowrap", SIZES[size] || SIZES.lg)} data-testid="price-total">
          {f.money(amount)}
        </span>
      </p>
      {context || rate ? (
        <p className="text-label-md text-touki-muted tabular-nums text-pretty" data-testid="price-detail">
          {/* What the total covers, then the rate on its own line: no
              separator is ever left alone at the end or the start of a line.
              The dot is kept in the text only (display: none), so copied
              text still reads "3 nuits, 2 voyageurs · 90 080 FCFA / nuit". */}
          {context ? <span className="block">{context}</span> : null}
          {context && rate ? <span className="hidden"> · </span> : null}
          {rate ? <span className="block whitespace-nowrap">{rate}</span> : null}
        </p>
      ) : null}
      {note ? <p className="text-caption text-touki-muted text-pretty" data-testid="price-note">{note}</p> : null}
      {debit ? <p className="text-caption text-touki-muted-strong tabular-nums" data-testid="price-charge">{t.charged(isolate(debit))}</p> : null}
    </div>
  );
}
